import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import styles from "./HeadacheCheck.module.css";
import {
  collection,
  doc,
  getDoc,
  query,
  where,
  getDocs,
  orderBy,
} from "firebase/firestore";
import { db } from "../utils/firebase";
import { getUserId, ensureUserId } from "../utils/userId";
import headacheData from "../data/headache.json";

interface HeadacheResponse {
  id: string; 
  userId: string;
  timestamp: any;
  answers: any[]; 
  totalScore: number;
  testType: string;
}

const HeadacheCheck: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  // TODO(종현): any -> 타이핑 지정하기
  const [responseHistory, setResponseHistory] = React.useState<HeadacheResponse[]>([]);

  const [isLoading, setIsLoading] = useState(true);

  const contentRef = useRef<HTMLDivElement>(null);
  const scrollTimerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const calculateTotalMaxRisk = (questions: any[]): number => {
    let count = 0;
    questions.forEach((q) => {
      // 1. 질문 자체에 riskFactor가 있거나, 옵션 중에 riskFactor가 있는 경우 1점 추가
      // (Q9-1처럼 옵션에만 riskFactor가 있는 경우도 포함하기 위함)
      const hasRisk = q.riskFactor || q.options?.some((opt: any) => opt.riskFactor);
      if (hasRisk) {
        count++;
      }

      // 2. 꼬리 질문(followUpIfYes)이 있다면 재귀적으로 탐색
      if (q.followUpIfYes) {
        count += calculateTotalMaxRisk(q.followUpIfYes);
      }
    });
    return count;
  };

  // [수정] 계산된 총 위험 요인 개수 (컴포넌트 로드 시 한 번 계산)
  const TOTAL_RISK_COUNT = React.useMemo(() => calculateTotalMaxRisk(headacheData.questions), []);

  useLayoutEffect(() => {
      document.documentElement.style.overflow = 'auto';
      document.body.style.overflow = 'auto';
  
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
  
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
  
      const timer = setTimeout(() => {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      }, 50);
  
      return () => clearTimeout(timer);
    }, []);

  const handleBack = () => {
    navigate(`${prefix}/check`);
  };

  const handleStartTest = () => {
    console.log("Navigating to /headache/check/new");
    localStorage.removeItem("headacheAnswers");
    navigate(`${prefix}/headache/check/new`, { state: {answers: [] }});
  };

  const handleResumeTest = (response: HeadacheResponse) => {
    console.log("Resuming test:", response.id);
    navigate(`${prefix}/headache/check/new`, {
      state: {
        answers: response.answers, 
        docId: response.id,     
      },
    });
  };

  const handleViewResult = (response: HeadacheResponse) => {
    console.log("Viewing test result:", response.id);
    
    navigate(`${prefix}/headache/check/results`, {
      state: {
        score: response.totalScore,
        answers: response.answers,
      },
    });
  };

  useEffect(() => {
    const getHeadacheCheckHistory = async () => {
      try {
        const userId = await ensureUserId();
        if (!userId) {
          console.error("User ID is missing");
          setIsLoading(false);
          return;
        }

        const q = query(
          collection(db, "userHeadacheResponse"),
          where("userId", "==", userId),
          orderBy("timestamp", "desc")
        );

        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          console.log("No headache check history found. Redirecting to new test...");
          navigate(`${prefix}/headache/check/new`, { replace: true });
          return;
        }

        const allResponses = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as HeadacheResponse[];

        const completedResponses = allResponses.filter(
          (response) => typeof response.totalScore === 'number' && response.totalScore > 0
        );

        if (completedResponses.length === 0) {
          // 쿼리 결과는 있지만, '완료된' 히스토리가 없는 경우
          console.log("No *completed* headache history found. Redirecting to new test...");
          navigate(`${prefix}/headache/check/new`, { replace: true });
        } else {
          // 완료된 히스토리가 있으면, 페이지 표시
          setResponseHistory(completedResponses);
          setIsLoading(false);
        }

      } catch (error) {
        console.error("Error fetching headache check history:", error);
        setIsLoading(false);
      }
    };

    const timer = setTimeout(() => {
      getHeadacheCheckHistory();
    }, 300); 

    return () => clearTimeout(timer);
  }, [navigate, prefix]);

  useEffect(() => {
    const contentElement = contentRef.current;
    if (!contentElement) return;

    const handleScroll = () => {
      contentElement.classList.remove(styles.scrollHidden);

      clearTimeout(scrollTimerRef.current);

      scrollTimerRef.current = setTimeout(() => {
        contentElement.classList.add(styles.scrollHidden);
      }, 400);
    };

    contentElement.addEventListener("scroll", handleScroll);
    contentElement.classList.add(styles.scrollHidden);

    return () => {
      contentElement.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [contentRef]);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={handleBack}>
            <ArrowBack />
          </button>
          <span className={styles.headerText}>{t("check_before_1")}</span>
        </div>
        <div className={styles.content}>
          <div className={`${styles.contentScroll} ${styles.loadingContainer}`}>
             <div className={styles.spinner}></div>
             <div className={styles.loadingText}>
               {t('headache_loading', '두통 검사 기록을 확인하고 있습니다...')}
             </div>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t("check_before_1")}</span>
      </div>
      <div className={styles.content}>
        <div className={styles.contentScroll} ref={contentRef}>
          {responseHistory.length > 0 ? (
            <div className={styles.historyList}>
              {responseHistory.map((response) => {
                const currentLanguage = i18n.language;
                const timestamp =
                  typeof response.timestamp?.seconds === "number"
                    ? new Date(response.timestamp.seconds * 1000)
                    : new Date(); // fallback
                const formattedDate = timestamp.toLocaleDateString(currentLanguage, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                });

                const scoreToShow = response.totalScore ?? 0;
                const total = TOTAL_RISK_COUNT;
                const percentage = total === 0 ? 0 : (scoreToShow / total) * 100;

                let scoreClass = styles.scoreGreen; 
                if (percentage >= 60) { 
                  scoreClass = styles.scoreRed;
                } else if (percentage >= 30) { 
                  scoreClass = styles.scoreOrange;
                }

                return (
                  <div
                    key={response.id}
                    className={styles.historyItem}
                    onClick={() => handleViewResult(response)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className={styles.historyLeft}>
                      <div className={styles.historyDate}>{formattedDate}</div>
                    </div>
                    <div className={styles.historyScore}>
                      <div className={`${styles.scoreCircle} ${scoreClass}`}>
                        {scoreToShow}
                      </div>
                      <span className={styles.scoreTotalText}>
                        / {total}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={styles.noHistory}>No test results found.</div>
          )}
        </div>
      </div>
      <button className={styles.startTestButton} onClick={handleStartTest}>
        {t("start_test_button")}
      </button>
    </div>
  );
};

export default HeadacheCheck;
